import React from "react";
import { useAtomValue } from "jotai";
import { ReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface PeriodSelectorProps {
  selectedPeriod: ReportingPeriod | null;
  onSelectPeriod: (period: ReportingPeriod) => void;
  onClearSelection?: () => void;
  label: string;
  allowEmpty?: boolean;
}

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedPeriod,
  onSelectPeriod,
  onClearSelection,
  label,
  allowEmpty = false,
}) => {
  const reportingPeriods = useAtomValue(ReportingPeriodAtom);

  // Sort periods by date (newest first)
  const sortedPeriods = [...reportingPeriods].sort(
    (a, b) => new Date(b.period).getTime() - new Date(a.period).getTime()
  );

  const handleClearSelection = () => {
    if (onClearSelection) {
      onClearSelection();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{label}</label>
        {allowEmpty && selectedPeriod && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClearSelection}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
      
      <Select
        value={selectedPeriod?.reportingPeriodId || ""}
        onValueChange={(value) => {
          const period = sortedPeriods.find(p => p.reportingPeriodId === value);
          if (period) {
            onSelectPeriod(period);
          }
        }}
      >
        <SelectTrigger>
          <SelectValue 
            placeholder={selectedPeriod ? 
              new Date(selectedPeriod.period).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 
              "Select a reporting period"
            } 
          />
        </SelectTrigger>
        <SelectContent>
          {sortedPeriods.map((period) => (
            <SelectItem key={period.reportingPeriodId} value={period.reportingPeriodId}>
              <div className="flex items-center justify-between w-full">
                <span>
                  {new Date(period.period).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                <span className={`ml-2 text-xs px-2 py-1 rounded ${
                  period.isOpen ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {period.isOpen ? 'Open' : 'Closed'}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}; 